import { useCallback, useEffect, useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Check, Download, Film, Image as ImageIcon, ImagePlus, Loader2, RefreshCw, Sparkles, Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MyMediaPickerDialog } from '@/components/media/MyMediaPickerDialog';
import { ProFeatureDialog } from '@/components/ProFeatureDialog';
import { ProBadge } from '@/components/ProBadge';
import { AiCoverDialog, type AiCoverReferencePicker } from '@/components/cover/AiCoverDialog';
import { RenderedFrameScrubber } from '@/components/clips/cover/RenderedFrameScrubber';
import { useCoverImageSources } from '@/hooks/use-cover-image-sources';
import { useCoverFrameCapture } from '@/hooks/use-cover-frame-capture';
import { useSaveClipCover } from '@/hooks/use-save-clip-cover';
import { downloadCoverImage } from '@/lib/downloads/downloadCoverImage';
import {
  buildRenderedFrameCover,
  buildUploadCover,
  type ClipCoverImage,
} from '@/lib/cover/coverImage';
import {
  resolveClipCardCoverUrl,
  resolveClipCoverReferenceTime,
  resolveRenderedCoverAspect,
  type ClipCoverOverride,
} from '@/lib/cover/clipCardCover';
import { coverDownloadFileName } from '@/lib/cover/coverDownload';
import { probeMediaMetadata } from '@/lib/layers/probeMediaMetadata';
import { frameExtractorUrl } from '@/lib/quoteCarousel/frameExtractorUrl';
import { snapToExportFrame } from '@/lib/timeline/exportFrameTiming';
import { cn } from '@/lib/utils';

export interface ClipCoverDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clipId: string;
  clipName: string | null;
  /** `finished_video_url`: the rendered clip the frame comes from. */
  videoUrl: string;
  /** The card's current cover. */
  coverUrl: string | null;
  hasProAccess: boolean;
  /** Called with the saved cover url (null after a removal), even when the
   *  dialog closed while the save was in flight. */
  onSaved: (coverUrl: string | null) => void;
}

type View = 'main' | 'frame';
type DialogError = 'saveError' | 'downloadError';
type ProbeResult = { url: string; duration: number; width: number; height: number };

const ACCEPTED_IMAGE_TYPES = 'image/png,image/jpeg';
// A stalled metadata load never fires load or error: give up so the frame
// source shows as unavailable instead of keeping AI and the frame waiting.
const PROBE_TIMEOUT_MS = 15_000;

function probeRenderedVideo(url: string): Promise<ProbeResult> {
  const failed: ProbeResult = { url, duration: 0, width: 0, height: 0 };
  return new Promise((resolve) => {
    const timer = window.setTimeout(() => resolve(failed), PROBE_TIMEOUT_MS);
    probeMediaMetadata(url, 'video').then(
      (result) => {
        window.clearTimeout(timer);
        resolve({ url, ...result });
      },
      () => {
        window.clearTimeout(timer);
        resolve(failed);
      },
    );
  });
}

/**
 * "Thumbnail" for an already-rendered clip on /clips: pick a frame of the
 * rendered video, an image (My Media or the computer) or an AI cover, save it
 * as `clip.data.coverImage`, and download it for YouTube.
 *
 * Mount it keyed by clip id and only while open: uploads and captures that
 * finish after unmount are dropped by `useCoverImageSources`, so nothing lands
 * on another clip.
 */
export function ClipCoverDialog({
  open,
  onOpenChange,
  clipId,
  clipName,
  videoUrl,
  coverUrl,
  hasProAccess,
  onSaved,
}: ClipCoverDialogProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { saveCover, saving } = useSaveClipCover();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [probe, setProbe] = useState<ProbeResult | null>(null);
  const [view, setView] = useState<View>('main');
  // Scrubber time; null = the default (last picked frame, else the middle).
  const [frameTime, setFrameTime] = useState<number | null>(null);
  const [lastPickedTime, setLastPickedTime] = useState<number | null>(null);
  // This dialog's own saves win over the prop until the card re-renders it.
  const [override, setOverride] = useState<ClipCoverOverride>(null);
  const [replacing, setReplacing] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [failedPreviewUrl, setFailedPreviewUrl] = useState<string | null>(null);
  const [dialogError, setDialogError] = useState<DialogError | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);
  const [proGateOpen, setProGateOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void probeRenderedVideo(videoUrl).then((result) => {
      if (!cancelled) setProbe(result);
    });
    return () => {
      cancelled = true;
    };
  }, [videoUrl]);

  const probed = probe?.url === videoUrl ? probe : null;
  // null while probing; 0 when the rendered video could not be opened.
  const duration = probed ? probed.duration : null;
  const frameAvailable = duration !== null && duration > 0;
  const exportAspect = resolveRenderedCoverAspect({ width: probed?.width ?? 0, height: probed?.height ?? 0 });
  const currentCoverUrl = resolveClipCardCoverUrl({ fetchedUrl: coverUrl, override });
  const referenceTime = resolveClipCoverReferenceTime({ lastPickedTime, duration: duration ?? 0 });
  const scrubTime = frameTime ?? snapToExportFrame(referenceTime);

  const { captureFramedDataUrl, uploadFramedDataUrl } = useCoverFrameCapture({
    videoUrl: frameExtractorUrl(videoUrl),
    exportAspect,
  });

  const {
    busy,
    setBusy,
    error,
    setError,
    isActive,
    commitCandidate,
    uploadFile,
    commitCapturedFrame,
    uploadReference,
    commitAiCover,
  } = useCoverImageSources({
    onCommit: (cover) => {
      void persist(cover);
    },
    uploadFramedDataUrl,
    exportAspect,
    buildCapturedCover: (url) => buildRenderedFrameCover(url),
  });

  // The hook has no concurrency guard: every action is gated on this.
  const actionsDisabled = busy !== null || saving || downloading;

  const clearErrors = () => {
    setDialogError(null);
    setError(null);
  };

  const applySaved = (url: string | null) => {
    // The cover is saved: the card shows it even if this dialog has closed.
    onSaved(url);
    if (!isActive()) return;
    setOverride({ url });
    setReplacing(false);
  };

  const persist = async (cover: ClipCoverImage) => {
    const result = await saveCover(clipId, cover);
    if (result.ok) {
      applySaved(result.coverUrl ?? cover.url);
    } else if (isActive()) {
      setDialogError('saveError');
    }
  };

  const handleRemove = async () => {
    if (actionsDisabled) return;
    clearErrors();
    const result = await saveCover(clipId, null);
    if (result.ok) {
      applySaved(null);
    } else if (isActive()) {
      setDialogError('saveError');
    }
  };

  const handleDownload = async () => {
    if (actionsDisabled || !currentCoverUrl) return;
    clearErrors();
    setDownloading(true);
    let ok = false;
    try {
      ({ ok } = await downloadCoverImage({
        url: currentCoverUrl,
        fileName: coverDownloadFileName(clipName, t('clipCover.fileSuffix')),
      }));
    } catch {
      ok = false;
    }
    if (!isActive()) return;
    setDownloading(false);
    if (!ok) setDialogError('downloadError');
  };

  const openFrameView = () => {
    if (actionsDisabled || duration === 0) return;
    clearErrors();
    setFrameTime(null);
    setView('frame');
  };

  // "Usar este frame": a full-size capture of the rendered frame, uploaded
  // and saved without a source time (it is a time on the render, not on the
  // source video the editor prints).
  const handleUseFrame = async () => {
    if (actionsDisabled || !frameAvailable) return;
    const time = scrubTime;
    clearErrors();
    setBusy('capturing');
    let captured: { dataUrl: string; captureTime: number } | null = null;
    try {
      captured = await captureFramedDataUrl(time);
    } catch {
      captured = null;
    }
    if (!isActive()) return;
    if (!captured) {
      setBusy(null);
      setError('captureError');
      return;
    }
    await commitCapturedFrame(captured.dataUrl, captured.captureTime);
    if (!isActive()) return;
    // Back to the cover, where the save (or an upload error) shows; the frame
    // view reopens on this frame and the AI reference defaults to it.
    setLastPickedTime(time);
    setView('main');
  };

  // The AI reference: the same scrubber over the rendered clip, smaller so the
  // prompt stays in view.
  const renderAiReferencePicker = ({ time, onTimeChange, disabled }: AiCoverReferencePicker) => (
    <RenderedFrameScrubber
      videoUrl={videoUrl}
      duration={duration}
      exportAspect={exportAspect}
      time={time}
      onTimeChange={onTimeChange}
      disabled={disabled}
      maxStillHeight="min(260px, 32vh)"
    />
  );

  const loadVideoFrame = useCallback(
    async (time: number): Promise<string | null> => (await captureFramedDataUrl(time))?.dataUrl ?? null,
    [captureFramedDataUrl],
  );

  const handleAiClick = () => {
    if (actionsDisabled) return;
    clearErrors();
    if (!hasProAccess) {
      setProGateOpen(true);
      return;
    }
    setAiOpen(true);
  };

  const handleFile = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Reset so choosing the same file again re-fires change.
    event.target.value = '';
    if (!file || actionsDisabled) return;
    setDialogError(null);
    await uploadFile(file);
  };

  const statusLabel =
    busy === 'capturing'
      ? t('editor.coverImage.capturing')
      : busy === 'uploading'
        ? t('editor.coverImage.uploading')
        : saving
          ? t('clipCover.saving')
          : null;
  const errorLabel = dialogError
    ? t(`clipCover.${dialogError}`)
    : error
      ? t(`editor.coverImage.${error}`)
      : null;
  const showFrameView = view === 'frame' && duration !== 0;
  const showSources = !currentCoverUrl || replacing;

  const statusAndError = (
    <>
      {statusLabel && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground" role="status">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {statusLabel}
        </p>
      )}
      {errorLabel && (
        <p role="alert" className="text-xs text-destructive">
          {errorLabel}
        </p>
      )}
    </>
  );

  const sourceButtonClass = 'h-auto min-h-9 whitespace-normal px-2 py-1.5 text-xs leading-tight';

  const mainView = (
    <div className="space-y-3">
      <div className="flex justify-center rounded-md bg-muted/40 p-2">
        <div
          data-testid="clip-cover-preview"
          className={cn(
            'relative flex w-full items-center justify-center overflow-hidden rounded-md border border-border',
            currentCoverUrl ? 'bg-black' : 'bg-muted',
          )}
          style={{ aspectRatio: String(exportAspect), maxWidth: `calc(min(320px, 40vh) * ${exportAspect})` }}
        >
          {currentCoverUrl && failedPreviewUrl !== currentCoverUrl ? (
            <img
              src={currentCoverUrl}
              alt={t('editor.coverImage.previewAlt')}
              className="h-full w-full object-contain"
              onError={() => setFailedPreviewUrl(currentCoverUrl)}
            />
          ) : (
            <div className="flex flex-col items-center gap-2 px-3 text-center text-xs text-muted-foreground">
              <ImageIcon className="h-6 w-6" aria-hidden="true" />
              <span>{t('clipCover.empty')}</span>
            </div>
          )}
          {(busy !== null || saving) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/50">
              <Loader2 className="h-5 w-5 animate-spin text-white" aria-hidden="true" />
            </div>
          )}
        </div>
      </div>

      {currentCoverUrl && (
        <div className="grid grid-cols-3 gap-2">
          <Button
            type="button"
            size="sm"
            className={sourceButtonClass}
            data-testid="clip-cover-download"
            disabled={actionsDisabled}
            onClick={() => void handleDownload()}
          >
            {downloading ? (
              <Loader2 className="mr-1.5 h-3.5 w-3.5 shrink-0 animate-spin" aria-hidden="true" />
            ) : (
              <Download className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            )}
            {downloading ? t('clipCover.downloading') : t('clipCover.download')}
          </Button>
          <Button
            type="button"
            variant={replacing ? 'secondary' : 'outline'}
            size="sm"
            className={sourceButtonClass}
            data-testid="clip-cover-replace"
            aria-expanded={replacing}
            disabled={actionsDisabled}
            onClick={() => setReplacing((value) => !value)}
          >
            <RefreshCw className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('editor.coverImage.replace')}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={sourceButtonClass}
            data-testid="clip-cover-remove"
            disabled={actionsDisabled}
            onClick={() => void handleRemove()}
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('editor.coverImage.remove')}
          </Button>
        </div>
      )}

      {showSources && (
        <div className="space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={sourceButtonClass}
              data-testid="clip-cover-frame-source"
              disabled={actionsDisabled || duration === 0}
              onClick={openFrameView}
            >
              <Film className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('clipCover.frameSource')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className={sourceButtonClass}
              data-testid="clip-cover-choose-image"
              disabled={actionsDisabled}
              onClick={() => setPickerOpen(true)}
            >
              <ImagePlus className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('editor.coverImage.chooseImage')}
            </Button>
          </div>
          {/* Never disabled for free users: the PRO gate explains itself at
              click time. PRO users wait for the probe, which sets the shape
              and whether a reference frame exists. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            className={cn(sourceButtonClass, 'w-full')}
            data-testid="clip-cover-ai"
            disabled={actionsDisabled || (hasProAccess && duration === null)}
            onClick={handleAiClick}
          >
            <Sparkles className="mr-1.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            {t('editor.coverImage.ai.button')}
            <ProBadge className="ml-1.5 px-1.5 py-0 text-[10px]" />
          </Button>
          {duration === 0 && (
            <p className="text-xs text-muted-foreground" data-testid="clip-cover-frame-unavailable">
              {t('clipCover.frame.unavailable')}
            </p>
          )}
        </div>
      )}

      {statusAndError}

      <p className="text-xs leading-snug text-muted-foreground">{t('clipCover.reExportHint')}</p>
    </div>
  );

  const frameView = (
    <div className="space-y-3">
      <RenderedFrameScrubber
        videoUrl={videoUrl}
        duration={duration}
        exportAspect={exportAspect}
        time={scrubTime}
        onTimeChange={setFrameTime}
        disabled={actionsDisabled}
      />
      {statusAndError}
      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          type="button"
          variant="secondary"
          data-testid="clip-cover-back"
          disabled={actionsDisabled}
          onClick={() => {
            setError(null);
            setView('main');
          }}
        >
          {t('clipCover.frame.back')}
        </Button>
        <Button
          type="button"
          data-testid="clip-cover-use-frame"
          disabled={actionsDisabled || !frameAvailable}
          onClick={() => void handleUseFrame()}
        >
          {busy !== null ? (
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <Check className="mr-1.5 h-4 w-4" aria-hidden="true" />
          )}
          {t('clipCover.frame.use')}
        </Button>
      </DialogFooter>
    </div>
  );

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md" data-testid="clip-cover-dialog">
          <DialogHeader>
            <DialogTitle>{t(showFrameView ? 'clipCover.frame.title' : 'clipCover.title')}</DialogTitle>
            <DialogDescription>{t('clipCover.description')}</DialogDescription>
          </DialogHeader>
          {showFrameView ? frameView : mainView}
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_IMAGE_TYPES}
            className="hidden"
            data-testid="clip-cover-file-input"
            disabled={actionsDisabled}
            onChange={(event) => void handleFile(event)}
          />
        </DialogContent>
      </Dialog>
      <MyMediaPickerDialog
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        onSelect={(item) => {
          // Reusing an existing My Media image: no new user_medias row.
          setPickerOpen(false);
          if (actionsDisabled) return;
          clearErrors();
          commitCandidate(buildUploadCover(item.url));
        }}
        onUploadInstead={() => {
          setPickerOpen(false);
          fileInputRef.current?.click();
        }}
      />
      {/* Mounted only while open: the price anchor queries the plans. */}
      {proGateOpen && (
        <ProFeatureDialog
          open
          onOpenChange={setProGateOpen}
          onUpgrade={() => navigate('/plans')}
          title={t('editor.coverImage.ai.proDialog.title')}
          body={t('editor.coverImage.ai.proDialog.body')}
          dismissLabel={t('editor.coverImage.ai.proDialog.dismiss')}
          upgradeLabel={t('editor.coverImage.ai.proDialog.upgrade')}
          testIdPrefix="clip-cover-ai-pro"
        />
      )}
      {/* Mounted only while open: the generation hook's polling lives and
          dies with the dialog. */}
      {aiOpen && (
        <AiCoverDialog
          open
          onOpenChange={setAiOpen}
          clipId={clipId}
          clipTitle={clipName}
          exportAspect={exportAspect}
          referenceAvailable={frameAvailable}
          renderReferencePicker={renderAiReferencePicker}
          loadVideoFrame={loadVideoFrame}
          uploadReference={uploadReference}
          // On an export frame, so the capture matches the frame the scrubber shows.
          currentTime={snapToExportFrame(referenceTime)}
          onUseCover={commitAiCover}
        />
      )}
    </>
  );
}
